// Idle-state center panel for Build — the AnalyzeOverview counterpart.
// Landing view on first visit (zero claims, see effectiveBuildView); after
// that it's reachable from the BuildViewNest "Overview" row. Headline reads
// computeRecommendations (the workflow advisor) ONLY — the single selector
// shared with the Where-am-I panel.
// View cards are places, not jobs (no lifecycle badges); Critique is the
// one job entry and keeps its status badge.
import type { ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Columns2, FileInput, Grid3X3, Map, PenLine, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useClaims } from "@/hooks/useClaims";
import { useFieldDebate } from "@/hooks/useFieldDebate";
import { usePapersCount } from "@/hooks/usePapersCount";
import { useCurrentUser, isPaidPlan } from "@/hooks/useCurrentUser";
import { useWritingDraft } from "@/hooks/useWritingDraft";
import { useConceptSnapshot } from "@/hooks/useConceptSnapshot";
import { useAnalyzeToolState } from "@/hooks/useAnalyzeToolState";
import { STATUS_BADGE, TOOL_META } from "@/components/analyze/toolMeta";
import { computeRecommendations, recsForPhase, type WorkflowRecommendation } from "@/lib/workflowAdvisor";
import { NextStepCard } from "@/components/where-am-i/NextStepCard";

interface CardDef {
  key: string;
  title: string;
  icon: ReactNode;
  iconColor: string;
  value: string;
  meta: string;
  lockedReason?: string;
  statusBadge?: { className: string; label: string };
  search: Record<string, unknown>;
  /** Cards that leave the Build route (e.g. outline import) supply their own
   *  navigation; otherwise the Open button uses goTo(search). */
  onOpen?: () => void;
}

export function BuildOverview({ conceptId }: { conceptId: string }) {
  const navigate = useNavigate();
  const { data: claimsData } = useClaims(conceptId);
  const { data: debateMap } = useFieldDebate(conceptId);
  const { data: counts } = usePapersCount(conceptId);
  const { data: currentUser } = useCurrentUser();
  const isPaid = isPaidPlan(currentUser?.plan);
  const hasPapers =
    (counts?.foundational ?? 0) + (counts?.trawled ?? 0) + (counts?.scored ?? 0) > 0;
  const { draft } = useWritingDraft(conceptId, { enabled: isPaid });
  const { critique } = useAnalyzeToolState(conceptId);

  const claimCount = claimsData?.length ?? 0;
  const evidencedCount = claimsData?.filter((c) => c.evidence_count > 0).length ?? 0;

  // Advisor-driven "Next step" — same engine/component as Curate's frontier ➤,
  // the Analyze card, and the Where-am-I overlay. Scoped to build-phase recs so
  // the card guides building (Map / claims / evidence / critique / write /
  // outline) rather than yanking back to Curate; falls back to the global top
  // rec when the user isn't ready to build yet (no build rec is live).
  const snapshot = useConceptSnapshot(conceptId);
  const buildRecs: WorkflowRecommendation[] = snapshot
    ? recsForPhase(
        computeRecommendations(snapshot).filter((r) => r.status !== "blocked"),
        "build",
      )
    : [];
  const onActRec = (rec: WorkflowRecommendation) => {
    navigate({
      to: rec.action.to,
      params: { conceptId },
      search: rec.action.search ?? {},
    } as never);
  };

  const goTo = (search: Record<string, unknown>) =>
    navigate({
      to: "/concepts/$conceptId/build",
      params: { conceptId },
      search,
    });

  const cards: CardDef[] = [
    {
      key: "map",
      title: "Map",
      icon: <Map className="size-3.5" />,
      iconColor: "#cba6f7",
      value: "Sketch your argument as claims and connect evidence from your reading.",
      meta: `${claimCount} claims`,
      search: { view: "map" },
    },
    {
      key: "outline-import",
      title: "Import outline",
      icon: <FileInput className="size-3.5" />,
      iconColor: "#fab387",
      value:
        "Have a draft already? Turn its outline into claims, matched to papers already in your corpus.",
      meta: "from existing writing",
      lockedReason: !isPaid ? "paid plan" : !hasPapers ? "needs papers" : undefined,
      search: {},
      onOpen: () =>
        navigate({ to: "/concepts/$conceptId/import/outline", params: { conceptId } }),
    },
    {
      key: "matrix",
      title: "Matrix",
      icon: <Grid3X3 className="size-3.5" />,
      iconColor: "#74c7ec",
      value: "Track which claims have evidence and where coverage is thin.",
      meta: `${evidencedCount}/${claimCount} evidenced`,
      search: { view: "matrix" },
    },
    {
      key: "split",
      title: "Split",
      icon: <Columns2 className="size-3.5" />,
      iconColor: "#94e2d5",
      value: "Work on your map beside the field's camps and tensions.",
      meta: "map + debate",
      lockedReason: debateMap ? undefined : "needs Field Debate",
      search: { view: "split" },
    },
    {
      key: "write",
      title: "Write",
      icon: <PenLine className="size-3.5" />,
      iconColor: "#a6e3a1",
      value:
        "Draft prose from your map — claims become sections, attached evidence becomes citations.",
      meta: isPaid ? (draft?.content ? "draft · started" : "no draft") : "",
      lockedReason: isPaid ? undefined : "paid plan",
      search: { view: "write" },
    },
    {
      key: "critique",
      title: "Argument Critique",
      icon: <Scale className="size-3.5" />,
      iconColor: "#cba6f7",
      value: TOOL_META.critique.value,
      meta: "",
      lockedReason:
        critique.aggregate.status === "locked" ? critique.aggregate.lockedReason : undefined,
      statusBadge: STATUS_BADGE[critique.aggregate.status],
      search: { view: "map", critique: true },
    },
  ];

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="mx-auto max-w-2xl space-y-4">
        {buildRecs[0] && <NextStepCard rec={buildRecs[0]} onAct={onActRec} />}

        <div className="px-1 pt-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Your build surfaces
        </div>
        {cards.map((c) => {
          const locked = !!c.lockedReason;
          return (
            <div
              key={c.key}
              data-build-card={c.key}
              className={`rounded-md border bg-background px-3 py-2.5 ${locked ? "opacity-60" : ""}`}
            >
              <div className="flex items-start gap-2.5">
                <span
                  className="inline-flex size-7 flex-none items-center justify-center rounded"
                  style={{ background: `${c.iconColor}29`, color: c.iconColor }}
                >
                  {c.icon}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{c.title}</span>
                    {c.statusBadge && (
                      <span
                        className={`rounded px-1.5 py-px text-[8.5px] font-bold tracking-wider ${c.statusBadge.className}`}
                      >
                        {c.statusBadge.label}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{c.value}</p>
                  <div
                    className={`mt-1 text-[11px] ${
                      locked ? "text-muted-foreground/80" : "font-medium text-foreground/80"
                    }`}
                  >
                    {locked ? `🔒 ${c.lockedReason}` : c.meta}
                  </div>
                </div>
                {!locked && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => (c.onOpen ? c.onOpen() : goTo(c.search))}
                  >
                    Open
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
